import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { createB2bLeadPython, getB2bLeadById, updateB2bLead } from '../../redux/Slice/b2bLeadSlice';
import { toast } from 'sonner';
import { clearMessage } from '../../redux/Slice/message';
import { setLoading } from '../../redux/Slice/loaderSlice';
import { Grid, Typography, TextField, Button, FormControl, FormLabel, Paper, Box } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { styled } from '@mui/material/styles';

const API_URL = process.env.REACT_APP_API_URL;

// Styled component to hide the input element
const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const B2bViewDetails = () => {
    const params = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const id = params?.id;

    const message = useSelector((state) => state.message);
    const { b2bLeads } = useSelector((state) => state.b2bLead);
    console.log("b2bLead:", b2bLeads);

    const [mp3FileName, setMp3FileName] = useState('');
    const [imageFileName, setImageFileName] = useState('');
    const [isMp3Selected, setIsMp3Selected] = useState(false);
    const [isImageSelected, setIsImageSelected] = useState(false);

    const [response, setResponse] = useState(null);
    console.log("Response:", response);

    const parsedResponse = JSON.parse(response);

    const [formData, setFormData] = useState({
        final_schema: b2bLeads?.templateJson || {},  // Ensure it defaults to an empty object
        image_schema: '',
        mp3_file: null,
        image_file: null,
        text_prompt: '',
        instruction: '',
    });

    useEffect(() => {
        dispatch(setLoading(true));
        if (id) dispatch(getB2bLeadById({ l_id: id }));
        dispatch(setLoading(false));
    }, [dispatch, id]);

    useEffect(() => {
        if (message?.message) {
            toast(message.message);
            dispatch(clearMessage());
        }
    }, [message, dispatch]);

    useEffect(() => {
        if (b2bLeads?.templateJson) {
            setFormData((prevData) => ({
                ...prevData,
                final_schema: b2bLeads?.templateJson, // Update final_schema
            }));
        }
    }, [b2bLeads]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleFileChange = (e) => {
        const { name, files } = e.target;
        const file = files[0];
        setFormData({ ...formData, [name]: file });

        // Update file names and selection status
        if (name === 'mp3_file') {
            setMp3FileName(file.name);
            setIsMp3Selected(true);
        } else if (name === 'image_file') {
            setImageFileName(file.name);
            setIsImageSelected(true);
        }
    };

    const handleSubmit = (e) => {
        dispatch(setLoading(true));
        e.preventDefault();
        const data = new FormData();

        // Append final_schema as a stringified JSON
        data.append('final_schema', JSON.stringify(formData.final_schema));

        // Append other form data
        Object.entries(formData).forEach(([key, value]) => {
            if (key !== 'final_schema') {
                data.append(key, value);
            }
        });

        // Dispatch the action
        dispatch(createB2bLeadPython(data))
        .then((response) => {
            setResponse(response.payload.response);
            dispatch(setLoading(false));
         
        })
        .catch((error) => {
            console.error('Error:', error);
        });
    };

    const handleSave = () => {
        const formData = {
            templateJson: parsedResponse,
          };
        dispatch(setLoading(true));
        dispatch(updateB2bLead({l_id: id,formData:formData}))
        .then(() => {
           dispatch(setLoading(false));
           navigate("/leadtemplate");
        })
        .catch(() => {
          dispatch(setLoading(false));
        });
    }

    const renderFilePreview = (file) => {
        if (file) {
            const fileType = file.type.split('/')[0];
            if (fileType === 'image') {
                return <img src={URL.createObjectURL(file)} width={100} alt="img" />;
            } else if (fileType === 'audio') {
                return <audio controls><source src={URL.createObjectURL(file)} type="audio/mp3" /></audio>;
            }
        }
        return null;
    };

    const getLastFieldName = (fieldPath) => {
        const parts = fieldPath.split(".");
        return parts[parts.length - 1]; // Return the last element
    }; 

    // Render fields dynamically based on schema
    const renderField = (key, field, path = '') => {
        const fieldPath = path ? `${path}.${key}` : key;

        // Handle array
        if (Array.isArray(field)) {
            if (field.every(item => typeof item === 'string')) {
                // Array of strings
                return (
                    <fieldset key={fieldPath} style={{ marginBottom: '16px', padding: '16px' }}>
                        <legend>{key}</legend>
                        {field.map((item, index) => (
                            <div key={`${fieldPath}[${index}]`} style={{ marginBottom: '12px' }}>
                                <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={item}
                                sx={{
                                    '&:focus': {
                                        borderColor: 'primary.main',
                                    },
                                    transition: 'border-color 0.3s ease',
                                }}
                            />
                            </div>
                        ))}
                    </fieldset>
                );
            } 
            return (
                <fieldset key={fieldPath} style={{ marginBottom: '16px', padding: '16px' }} >
                    <legend>{key}</legend>
                    {field.map((item, index) => (
                        <div key={`${fieldPath}[${index}]`} style={{ marginBottom: '12px' }}>
                            {Object.entries(item).map(([nestedKey, nestedField]) =>
                                renderField(nestedKey, nestedField, `${fieldPath}[${index}]`)
                            )}
                        </div>
                    ))}
                </fieldset>
            );
        }

        // Handle object
        if (typeof field === 'object' && field !== null) {
            return (
                <fieldset key={fieldPath} style={{ marginBottom: '16px', padding: '16px' }} >
                    <legend>{key}</legend>
                    {Object.entries(field).map(([nestedKey, nestedField]) =>
                        renderField(nestedKey, nestedField, fieldPath)
                    )}
                </fieldset>
            );
        }

        // Handle simple field
        return (
            <div key={fieldPath} style={{ marginBottom: '12px' }}>
                <Typography variant="body1">{key}</Typography>
                <TextField
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={field}
                    sx={{
                        '&:focus': {
                            borderColor: 'primary.main',  // Changes border color on focus
                        },
                        transition: 'border-color 0.3s ease',  // Smooth transition for border color
                    }}
                />
            </div>
        );
    };

    return (
        <Grid container spacing={3}>
            <Grid item xs={12} md={6} >
                <Typography variant="h6" marginBottom={2}>
                    B2B Lead Details
                </Typography>

                {/* Image Schema Input */}
                <FormControl fullWidth>
                    <FormLabel>Image Schema *</FormLabel>
                    <TextField
                        placeholder="Image Schema"
                        name="image_schema"
                        value={formData.image_schema}
                        onChange={handleInputChange}
                        variant="outlined"
                        fullWidth
                        margin="normal"
                    />
                </FormControl>

                {/* Text Prompt and Instruction Input (Side by Side) */}
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <FormLabel>Text Prompt </FormLabel>
                            <TextField
                                placeholder="Text Prompt"
                                name="text_prompt"
                                value={formData.text_prompt}
                                onChange={handleInputChange}
                                variant="outlined"
                                fullWidth
                                multiline
                                margin="normal"
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <FormLabel>Instruction </FormLabel>
                            <TextField
                                placeholder="Instruction"
                                name="instruction"
                                value={formData.instruction}
                                onChange={handleInputChange}
                                variant="outlined"
                                fullWidth
                                multiline
                                margin="normal"
                            />
                        </FormControl>
                    </Grid>
                </Grid>

                {/* MP3 File Input (Side by Side with Image File Input) */}
                <Grid container spacing={2}>
                    {/* MP3 File Input */}
                    <Grid item xs={12} md={6}>
                        <Button
                            variant="outlined"
                            startIcon={<CloudUploadIcon />}
                            component="label"
                            fullWidth
                            sx={{
                                height: 60,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginTop: 2,
                                position: 'relative',
                                overflow: 'hidden',
                                textAlign: 'center',
                                padding: 0,
                            }}
                        >
                            {!isMp3Selected && 'Upload MP3 File'}

                            <VisuallyHiddenInput
                                type="file"
                                name="mp3_file"
                                // accept="audio/mp3"
                                onChange={handleFileChange}
                            />
                        </Button>

                        {/* Display the selected MP3 file name and preview if selected */}
                        {isMp3Selected && (
                            <div style={{ marginTop: 8 }}>
                                {renderFilePreview(formData.mp3_file)}
                                <Typography variant="body2" color="textSecondary">{mp3FileName}</Typography>
                            </div>
                        )}
                    </Grid>

                    {/* Image File Input */}
                    <Grid item xs={12} md={6}>
                        <Button
                            variant="outlined"
                            startIcon={<CloudUploadIcon />}
                            component="label"
                            fullWidth
                            sx={{
                                height: isImageSelected ? 120 : 60,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginTop: 2,
                                position: 'relative',
                                overflow: 'hidden',
                                textAlign: 'center',
                                padding: 0,
                            }}
                        >
                            {!isImageSelected && 'Upload Image'}

                            {/* Render image preview inside the button */}
                            {isImageSelected && (
                                <div style={{
                                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', justifyContent: 'center', alignItems: 'center',
                                }}>
                                    <img
                                        src={URL.createObjectURL(formData.image_file)}
                                        alt="Selected"
                                        style={{ maxHeight: '80%', maxWidth: '100%', objectFit: 'contain' }}
                                    />
                                </div>
                            )}

                            <VisuallyHiddenInput
                                type="file"
                                name="image_file"
                                accept="image/*"
                                onChange={handleFileChange}
                            />
                        </Button>

                        {isImageSelected && (
                            <div style={{ marginTop: 8 }}>
                                <Typography variant="body2" color="textSecondary">{imageFileName}</Typography>
                            </div>
                        )}
                    </Grid>
                </Grid>

                {/* Submit Button */}
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    sx={{ marginTop: 2 }}
                >
                    Submit
                </Button>
            </Grid>

            <Grid item xs={12} md={6}>
                <Paper elevation={3} sx={{ padding: 3 }}>
                    <Typography variant="h6" marginBottom={2}>
                        Final Schema Details
                    </Typography>

                    {/* Scrollable Container for Fields */}
                    <Box sx={{
                        maxHeight: '60vh',
                        overflowY: 'auto',
                        padding: '16px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                    }}>
                       {/* Render final schema fields dynamically */}
                        {response ? (
                           Object.entries(parsedResponse).map(([key, field]) =>
                            renderField(key, field)
                        )
                        ) : (
                            Object.entries(formData.final_schema).map(([key, field]) =>
                                renderField(key, field)
                            )
                        )}
                    </Box>
                    <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    sx={{ marginTop: 2 }}
                >
                    Save
                </Button>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default B2bViewDetails;
